import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Menu, MenuItem, Chip, TextField, Pagination } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import MainMenuDrawer from '../../components/Main/MenuDrawer';
import Header from '../../components/common/Header';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getNewsListApi } from '../../api';
import { PATHS } from '../../routes/paths';

const formatRelativeTime = (createdAt) => {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) return '';

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 1) return '방금 전';
  if (diffMinutes < 60) return `${diffMinutes}분 전`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}시간 전`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}일 전`;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const PAGE_LIMIT = 10;

const NewsList = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); 
  const [filterType, setFilterType] = useState('titleContent'); 
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [newsItems, setNewsItems] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const getPageFromSearch = useCallback(() => {
    const raw = searchParams.get('page');
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }, [searchParams]);
  const [page, setPage] = useState(getPageFromSearch);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) {
        setCurrentUser(JSON.parse(raw));
      } else {
        setCurrentUser(null);
      }
    } catch {
      setCurrentUser(null);
    }
  }, []);

  const filterOptions = [
    { key: 'titleContent', label: '제목+내용' },
    { key: 'title', label: '제목' },
    { key: 'content', label: '내용' },
    { key: 'header', label: '말머리' },
  ];

  const currentFilterLabel =
    filterOptions.find((opt) => opt.key === filterType)?.label || '제목+내용';

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (key) => {
    setFilterType(key);
    setFilterAnchorEl(null);
    setPage(1);
    updatePageParam(1);
  };

  const sortValue = activeTab === 'popular' ? 'popular' : 'latest';
  const isLoggedIn = Boolean(currentUser);

  const updatePageParam = useCallback(
    (nextPage) => {
      const params = new URLSearchParams(searchParams);
      if (nextPage > 1) {
        params.set('page', String(nextPage));
      } else {
        params.delete('page');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const loadNews = useCallback(async (targetPage = 1) => {
    try {
      setError(null);

      const params = {
        filterType,
        sort: sortValue,
        page: targetPage,
        limit: PAGE_LIMIT,
      };

      const trimmedKeyword = keyword.trim();
      if (trimmedKeyword) {
        params.keyword = trimmedKeyword;
      }

      const res = await getNewsListApi(params);
      const data = res?.data || {};
      const items = data.items || [];
      setNewsItems(items);
      setTotal(data.total ?? items.length ?? 0);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load news list:', err);
      setError(err.message || '목록을 불러오지 못했습니다.');
    } finally {
      // no-op
    }
  }, [filterType, sortValue, keyword]);

  const handleSearch = () => {
    setKeyword(keywordInput.trim());
    setPage(1);
    updatePageParam(1);
  };

  const handleClickWrite = () => {
    navigate(PATHS.newsWrite);
  };

  useEffect(() => {
    loadNews(page);
  }, [loadNews, page]);

  useEffect(() => {
    const nextPage = getPageFromSearch();
    if (nextPage !== page) {
      setPage(nextPage);
    }
  }, [getPageFromSearch, page]);

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          pb: 2,
          position: 'relative',
        }}
      >
        <MainMenuDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          onNavigate={handleNavigate}
        />

        <Header
          variant="full"
          onClickMenu={() => setMenuOpen(true)}
        />

        <Box sx={{ mb: 2, mt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, fontSize: 24 }}>
            마닐라 소식
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {[
              { key: 'all', label: '전체글' },
              { key: 'popular', label: '인기글' },
            ].map((tab) => {
              const selected = activeTab === tab.key;
              return (
                <Box
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setPage(1);
                    updatePageParam(1);
                  }}
                  sx={{
                    px: 1.8,
                    py: 0.6,
                    borderRadius: 12,
                    border: '1px solid',
                    borderColor: selected ? '#3C93E9' : '#e0e0e0',
                    bgcolor: selected ? '#F5FAFF' : '#ffffff',
                    color: selected ? '#3C93E9' : '#333333',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {tab.label}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ mt: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                display: 'inline-flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: 1.5,
                border: '1px solid #e0e0e0',
                px: 1,
                height: 40,
                cursor: 'pointer',
                width: 110,
                flexShrink: 0,
              }}
              onClick={handleFilterClick}
            >
              <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 500, pl: 1 }}>
                {currentFilterLabel}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 18, ml: 0.5 }} />
            </Box>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="검색어를 입력해주세요"
              size="small"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ fontSize: 20, color: '#9ca3af', mr: 0.5 }} />,
                sx: {
                  borderRadius: 2,
                  fontSize: 13,
                },
              }}
            />
          </Box>

          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={handleFilterClose}
            disableScrollLock 
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                mt: 0,
                width: 110,
              },
            }}
          >
            {filterOptions.map((opt) => (
              <MenuItem
                key={opt.key}
                selected={filterType === opt.key}
                onClick={() => handleFilterSelect(opt.key)}
                sx={{ fontSize: 14, fontWeight: 500 }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>

          <Box sx={{ mt: 1 }}>
            {!error && newsItems.length === 0 && (
              <Typography variant="body2" sx={{ color: '#999999', mt: 2 }}>
                등록된 마닐라 소식이 없습니다.
              </Typography>
            )}

            {!error &&
              newsItems.map((item) => (
                <Box
                  key={item.news_id}
                  sx={{
                    py: 1.5,
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    navigate(`/news/${item.news_id}`, {
                      state: { from: `${location.pathname}${location.search}` },
                    })
                  }
                >
                  <Chip
                    label={item.header_name || '기타'}
                    size="small"
                    color="default"
                    sx={{
                      borderRadius: 1,
                      bgcolor: item.header_color || '#F0EFED',
                      mb: 0.5,
                    }}
                  />

                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: 15 }}>
                    {item.title}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mt: 0.5,
                      justifyContent: 'space-between',
                      fontSize: 11,
                      color: '#999999',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <ThumbUpOffAltIcon sx={{ fontSize: 16 }} />
                        <span>{item.recommend_count}</span>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Box
                          component="img"
                          src="/icon/chat_icon(2).png"
                          alt="댓글 아이콘"
                          sx={{ width: 14, height: 14 }}
                        />
                        <span>{item.comment_count}</span>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                        <span>{item.view_count}</span>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{ fontSize: 11, color: '#b0b0b0' }}>
                      {formatRelativeTime(item.created_at)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_event, value) => {
                    setPage(value);
                    updatePageParam(value);
                  }}
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            )}
          </Box>
        </Box>

        {isLoggedIn && (
          <Box
            onClick={handleClickWrite}
            sx={{
              position: 'fixed',
              bottom: 50,
              right: 'max(24px, calc((100vw - 430px) / 2 + 24px))',
              minWidth: 72,
              height: 40,
              px: 2.5,
              py: 1,
              borderRadius: 999,
              bgcolor: '#3679FF',
              color: '#ffffff',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: '0 6px 12px rgba(0,0,0,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 1200,
            }}
          >
            글쓰기
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NewsList;


